'use client';

import React from 'react';

import useCarouselAnimation from '@src/hooks/useCarouselAnimation';
import useCarouselIndicatorAnimation from '@src/hooks/useCarouselIndicatorAnimation';

import CarouselContainer from '@src/components/Shared/CarouselContainer';
import CarouselIndicators from '@src/components/Shared/CarouselIndicators';
import NextImage from '@src/components/Shared/NextImage';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import InspectorWrapper from '@src/components/Shared/InspectorWrapper';

import Twitter from '@src/components/Icons/Twitter';
import LinkedIn from '@src/components/Icons/LinkedIn';
import Instagram from '@src/components/Icons/Instagram';
import NextLink from '../NextLink';

import { AuthorsPropsTypes } from './Authors.types';

const Authors: React.FC<AuthorsPropsTypes> = ({ authors, entryId, fieldId, className = '' }) => {
  const { carouselRef, containerRef, handleAddCarouselItemsToRef, activeIndex, handleJumpToSlide, maxIndex } =
    useCarouselAnimation({
      overrideMaxIndex: authors?.length ? authors.length - 1 : 0,
    });

  const { handleAddIndicatorRef } = useCarouselIndicatorAnimation({});

  if (!authors?.length) return null;

  return (
    <InspectorWrapper entryId={entryId} fieldId={fieldId}>
      <section className={`authors-component bg-white mb-[2.4rem] ${className}`}>
        {/* Mobile / tablet carousel */}
        <div ref={containerRef} className='md:hidden'>
          <CarouselContainer carouselRef={carouselRef}>
            {authors.map((author) => {
              return (
                <article
                  key={author?.sys?.id}
                  ref={handleAddCarouselItemsToRef}
                  className='relative flex min-w-full flex-col not-last:mr-[5rem]'
                >
                  <div className='relative mx-auto w-full'>
                    <div className='flex items-center'>
                      {author?.profile?.url && (
                        <NextImage
                          className='mx-auto mb-[1.6rem] w-[55%] [&>img]:mr-[-1.3rem]'
                          priority
                          entryId={author?.sys?.id}
                          fieldId='profile'
                          src={author?.profile?.url}
                          alt={author?.profile?.description ?? name}
                          width={author?.profile?.width}
                          aspectRatio={
                            author?.profile?.width && author?.profile?.height
                              ? author.profile.width / author.profile.height
                              : undefined
                          }
                          objectFit='contain'
                        />
                      )}
                      <div className='mt-[-15%] flex flex-col [&>a:not(:last-child)]:mb-[3rem]'>
                        {author?.twitter && (
                          <NextLink href={author?.twitter}>
                            <Twitter className='h-[1.7rem] w-[1.7rem]' />
                          </NextLink>
                        )}

                        {author?.linkedin && (
                          <NextLink href={author?.linkedin}>
                            <LinkedIn className='h-[1.7rem] w-[1.7rem]' />
                          </NextLink>
                        )}

                        {author?.instagram && (
                          <NextLink href={author?.instagram}>
                            <Instagram className='h-[1.7rem] w-[1.7rem]' />
                          </NextLink>
                        )}
                      </div>
                    </div>

                    <RenderBodyCopy
                      entryId={author?.sys?.id}
                      bodyCopy={author?.bodyCopy?.json?.content}
                      className='text-[1.5rem]  font-light leading-[2.2rem] text-body-copy--dark md:text-[1.3rem] md:leading-[2.4rem] [&>p]:leading-[2.7rem] [&_a]:underline [&_a]:underline-offset-[0.2rem] [&_strong]:font-medium [&_strong]:text-primary'
                    />

                    <hr className='my-[1.6rem] h-[0.01rem] w-full text-[rgba(0,0,0,0.3)]' />

                    <div className='flex items-center gap-[1.2rem] text-[1.5rem]'>
                      {author?.name && <span className='font-medium leading-[1.6rem] text-primary'>{author?.name}</span>}

                      {author?.name && author?.qualifications && (
                        <span className='h-[0.1rem] w-[1.8rem] bg-primary md:w-[2.4rem]' />
                      )}

                      {author?.qualifications && (
                        <span className='font-light leading-[1.6rem] text-body-copy--dark'>
                          {author?.qualifications}
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </CarouselContainer>

          {authors.length > 1 && (
            <CarouselIndicators
              className='relative z-10 mt-[3.5rem] flex justify-center'
              maxIndex={maxIndex}
              handleJumpToSlide={handleJumpToSlide}
              handleAddIndicatorRef={handleAddIndicatorRef}
              activeIndex={activeIndex}
            />
          )}
        </div>

        {/* Desktop stacked layout */}
        <div className='hidden md:block'>
          <div className='mx-auto flex flex-col gap-[2.4rem]'>
            {authors.map((author) => {
              return (
                <article key={author?.sys?.id} className='grid grid-cols-[17.1rem_1fr] items-center gap-[2.4rem]'>
                  <div className='shrink-0'>
                    {author?.profile?.url && (
                      <NextImage
                        priority
                        entryId={author?.sys?.id}
                        fieldId='profile'
                        src={author?.profile?.url}
                        alt={author?.profile?.description ?? name}
                        width={author?.profile?.width}
                        aspectRatio={
                          author?.profile?.width && author?.profile?.height
                            ? author.profile.width / author.profile.height
                            : undefined
                        }
                        objectFit='contain'
                      />
                    )}
                  </div>

                  <div className='max-w-[78rem]'>
                    <RenderBodyCopy
                      entryId={author?.sys?.id}
                      bodyCopy={author?.bodyCopy?.json?.content}
                      className='font-light leading-[2.56rem] text-body-copy--dark [&_a]:underline [&_a]:underline-offset-[0.2rem] [&_strong]:font-medium [&_strong]:text-primary'
                    />

                    <div className='mb-[1.5rem] mt-[1.8rem] h-[0.1rem] w-full bg-[rgba(0,0,0,0.18)]' />

                    <div className='flex items-center justify-between gap-[2.4rem] leading-[0]'>
                      <div className='flex items-center gap-[1rem] text-[1.8rem]'>
                        {author?.name && <span className='font-medium leading-[0] text-primary'>{author?.name}</span>}

                        {author?.name && author?.qualifications && <span className='text-primary'>—</span>}

                        {author?.qualifications && (
                          <span className='font-light leading-[0] text-body-copy--dark'>{author?.qualifications}</span>
                        )}
                      </div>

                      <div className='flex [&>a:not(:last-child)]:mr-[1.7rem]'>
                        {author?.twitter && (
                          <NextLink href={author?.twitter}>
                            <Twitter className='h-[1.7rem] w-[1.7rem] text-primary' />
                          </NextLink>
                        )}

                        {author?.linkedin && (
                          <NextLink href={author?.linkedin}>
                            <LinkedIn className='h-[1.7rem] w-[1.7rem] text-primary' />
                          </NextLink>
                        )}

                        {author?.instagram && (
                          <NextLink href={author?.instagram}>
                            <Instagram className='h-[1.7rem] w-[1.7rem] text-primary' />
                          </NextLink>
                        )}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </InspectorWrapper>
  );
};

export default Authors;
